import { EventListItem } from '../types';

/**
 * Get Istanbul default coordinates
 */
export const getIstanbulCoordinates = (): { lat: number; lng: number } => {
  return { lat: 41.0082, lng: 28.9784 };
};

/**
 * Calculate map bounds from events
 * Returns center coordinates and zoom level to fit all event markers
 */
export const calculateMapBounds = (
  events: EventListItem[]
): { center: { lat: number; lng: number }; zoom: number } => {
  // Filter events with valid coordinates
  const eventsWithCoords = events.filter((event) => {
    if (event.isOnline === "1") return false;
    if (!event.locationLatitude || !event.locationLongitude) return false;
    
    const lat = parseFloat(event.locationLatitude);
    const lng = parseFloat(event.locationLongitude);
    
    return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
  });

  // If no events with coordinates, return Istanbul default
  if (eventsWithCoords.length === 0) {
    return {
      center: getIstanbulCoordinates(),
      zoom: 12,
    };
  }

  // If only one event, center on it
  if (eventsWithCoords.length === 1) {
    const event = eventsWithCoords[0];
    return {
      center: {
        lat: parseFloat(event.locationLatitude!),
        lng: parseFloat(event.locationLongitude!),
      },
      zoom: 15,
    };
  }

  // Calculate bounds from all events
  const latitudes = eventsWithCoords.map((e) => parseFloat(e.locationLatitude!));
  const longitudes = eventsWithCoords.map((e) => parseFloat(e.locationLongitude!));

  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);

  // Calculate center
  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;

  // Calculate zoom level based on bounds
  // This is a simplified calculation - Google Maps will handle fine-tuning
  const latDiff = maxLat - minLat;
  const lngDiff = maxLng - minLng;
  const maxDiff = Math.max(latDiff, lngDiff);

  let zoom = 15;
  if (maxDiff > 0.1) zoom = 11;
  else if (maxDiff > 0.05) zoom = 12;
  else if (maxDiff > 0.02) zoom = 13;
  else if (maxDiff > 0.01) zoom = 14;
  else zoom = 15;

  return {
    center: { lat: centerLat, lng: centerLng },
    zoom,
  };
};
